/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { sortItem } from '../Page/PageManager';
import { defaultValue } from './Folder';
import { analyzeFolder } from './FolderAnalyze';

const FolderManager: ComponentManagerFn<'Folder'> = (platform: Platform) => {
  return {
    ...baseComponentManager<'Folder'>(platform, 'Folder'),
    type: 'Folder',
    name: 'Folder',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'Comments',
          'DataStore',
          'Drawer',
          'Folder',
          'CloudDataStore',
          'LocalDataStore',
          'Popup',
          'ReadOnlyDataStore',
          'Transaction',
          'TransientDataStore',
        ] as ItemType[]
      ).includes(childType),
    canHaveChildren: () => true,
    category: 'Containers',
    layout: 'sizeAware',
    onChildMoveIn: ({ page, value }) => {
      // DataStore must be on top before any other item type
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          return sortItem(aItem, bItem, [
            'Folder',
            'CloudDataStore',
            'DataStore',
            'LocalDataStore',
            'ReadOnlyDataStore',
            'TransientDataStore',
            'Transaction',
            'Comments',
            'DataStore',
            'Popup',
          ]);
        }
        return 0;
      });
    },
    defaultValue,
    props: {
      name: {
        label: 'Name',
        type: 'string',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
    },
    getDisplayText: (item) => item.props.name,
    icon: (item) => item.props.icon ?? { iconName: 'folder' },
    analyze: analyzeFolder,
    description: `**Overview**<br/>
The Folder is a logical container designed strictly for organization and maintainability. Unlike layout components (such as Flex Panels or Box Containers), the Folder does not render any visual UI on the final page. Instead, it exists solely within the designer's component tree to help you group and manage infrastructure components.
As applications grow, a single page might contain dozens of Data Stores, Popups, and Transactions. The Folder component allows you to group these related items (e.g., "User Management Logic" or "Order Processing Data"), keeping your project structure clean and navigable.

**1. Infrastructure Organization**<br/>
The Folder is specifically engineered to hold "Page-Level" components—elements that function in the background or appear as overlays rather than sitting in the main layout flow.
* **Allowed Content:** You can drag and drop the following components into a Folder:
    * **Data & State:** Data Store, Cloud Data Store, Local Data Store, ReadOnly Data Store
    * **Overlays:** Popup, Drawer
    * **Logic:** Transaction
    * **Collaboration:** Comments
* **Nesting:** You can place Folders inside other Folders to create deep hierarchies.

**2. Automatic Sorting**<br/>
To maintain a consistent and readable structure across your team, the Folder enforces an internal sorting rule. When you add items to a Folder, they are automatically reordered based on their type:
1. Folders (Always at the top)
2. Data Stores (Cloud, Local, etc.)
3. Transactions
4. Overlays (Popups, Drawers)
* **Note:** This automatic behavior ensures that critical data definitions are always visible at the start of the list, regardless of when they were added.

**3. Identification**<br/>
Since Folders are purely for developer convenience, their properties focus on distinct identification within the design environment.
* **Name:** A human-readable label (e.g., "Customer API Stores"). Use descriptive names to help other developers understand the purpose of the group immediately.
* **Icon:** Select a custom icon to visually distinguish the folder in the component tree. For example, use a "database" icon for a folder containing Data Stores, or a "window" icon for a folder containing Popups.`,
  };
};

export default FolderManager;
